import React, { useState } from 'react';
import styled from 'styled-components';
import { db } from '../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Container, Row, Col, Badge, Modal, Form, Spinner, Button } from 'react-bootstrap';
import withAuthContext from '../context/auth/AuthConsumer';
import { IAuthContext } from '../context/auth/auth-context';
import Footer from '../components/Footer';
import { Edit } from 'react-feather';
import { toast } from 'react-toastify';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import { TIERS } from '../types/tiers';
import * as R from 'ramda';
import mountainHeroBg from '../assets/mountain-6.jpg';

const PageBackground = styled.div`
  min-height: calc(100vh - 120px);
  padding: 18px 0 24px;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.52) 0%, rgba(0, 0, 0, 0.66) 48%, rgba(0, 0, 0, 0.75) 100%), url(${mountainHeroBg});
  background-position: center 32%;
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: fixed;
`;

const PageWrapper = styled.div`
  display: block;
  width: 100%;
  position: relative;
  max-width: 800px;
  margin: 1rem auto;
`;

const AccountCard = styled.div`
  background: rgba(10, 10, 12, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);

  .MuiDivider-root {
    background-color: rgba(148, 163, 184, 0.22);
  }
`;

const TierBadge = styled(Badge)``;

const PageTitle = styled.h1`
  display: block;
  font-size: 28px;
  text-align: center;
  text-transform: uppercase;
  padding: 30px 0 60px;
  color: #f8fafc;
  letter-spacing: 0.02em;
`;

const Paragraph = styled.p`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  font-size: 18px;
  margin: 8px 0;
  color: #94a3b8;

  b {
    color: #f8fafc;
    font-weight: 600;
  }

  a {
    color: #34d399;
  }
`;
const EditUserPanelBody = styled(Modal.Body)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const EditUserPanelItem = styled.div`
  margin-bottom: 30px;
`;

const EditUserModal = styled(Modal)`
  .modal-dialog {
    max-width: 520px;
    width: 90%;
  }

  .modal-content {
    background: rgba(7, 10, 13, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
    padding: 24px;
    max-height: 80vh;
    overflow-y: auto;
  }

  .modal-header {
    border-bottom: 0;
    padding: 0 0 14px;
  }

  .modal-title {
    color: #f8fafc;
    font-size: 20px;
    font-weight: 600;
  }

  .modal-body {
    padding: 0;
  }

  .modal-footer {
    border-top: 0;
    padding: 14px 0 0;
    gap: 10px;
  }

  .form-label {
    color: #94a3b8;
    font-weight: 600;
    font-size: 12px;
  }

  .form-control {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #ecf0f5;
  }

  .form-control:focus {
    box-shadow: none;
    border-color: rgba(52, 211, 153, 0.35);
    background: rgba(255, 255, 255, 0.04);
    color: #ecf0f5;
  }
`;

const EditUserCloseButton = styled(Button)`
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: #ecf0f5;
`;

const EditUserSaveButton = styled(Button)`
  border-radius: 10px;
  border: 0;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #022c22;
  font-weight: 700;
`;
const UpdateProfile = styled.div`
  margin-bottom: 15px;
  text-align: right;
  & button {
    padding-bottom: 8px;
  }
`;

const UpdateProfileButton = styled(Button)`
  margin-left: 1rem;
  background: #3aefb8;
  border: 1px solid #34d399;
  color: #000 !important;
  border-radius: 12px;
  padding: 0.45rem 0.9rem;
  font-size: 14px;
  font-weight: 700;
  font-family: 'DM Sans', -apple-system, sans-serif;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 8px 20px rgba(52, 211, 153, 0.28);
  transition: background 0.15s ease, transform 0.12s ease;

  &:hover,
  &:focus,
  &:active {
    background: #34d399 !important;
    border-color: #10b981 !important;
    color: #000 !important;
    transform: translateY(-1px);
    box-shadow: 0 0 0 0.2rem rgba(16, 185, 129, 0.25) !important;
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      margin: 0,
    },
  })
);

const Account: React.FC<{
  context: IAuthContext;
}> = (props) => {
  const classes = useStyles();
  const authContext = props.context;
  const [isLoading, setIsLoading] = useState(false);
  const [editUserNameModal, setEditUserNameModal] = useState<boolean>(false);
  const userinfo: any = authContext.getCompleteUserInfo();
  const [userName, setUserName] = useState(
    authContext.getUserName() ? authContext.getUserName() : authContext.getUserDisplayName()
  );
  const [userPhone, setUserPhone] = useState(authContext.getUserPhone());
  const [clientName, setClientName] = useState(authContext.getClientName());
  const [clientAddress, setClientAddress] = useState(authContext.getClientAddress());

  const creationTime = () => {
    if (authContext && authContext.user) {
      return authContext.user.metadata.creationTime;
    }
  };
  const renderTierBadge = (tier: TIERS, BadgeComp = TierBadge) => {
    return R.cond<any, any>([
      [
        R.equals(TIERS.DAYPASS),
        () => (
          <BadgeComp pill variant="primary">
            DAYPASS
          </BadgeComp>
        ),
      ],
      [
        R.equals(TIERS.PLATINUM),
        () => (
          <BadgeComp pill variant="primary">
            PLATINUM
          </BadgeComp>
        ),
      ],
      [
        R.equals(TIERS.BASIC),
        () => (
          <BadgeComp pill variant="info">
            BASIC
          </BadgeComp>
        ),
      ],
      [
        R.equals(TIERS.FREE),
        () => (
          <BadgeComp pill variant="success">
            FREE
          </BadgeComp>
        ),
      ],
      [
        R.equals(TIERS.SUPER_ADMIN),
        () => (
          <BadgeComp pill variant="primary">
            Super Admin
          </BadgeComp>
        ),
      ],
      [
        R.T,
        () => (
          <BadgeComp pill variant="danger">
            N/A
          </BadgeComp>
        ),
      ],
    ])(tier);
  };

  const updateUserNameInContext = async (name: any) => {
    authContext.getUserName = () => name;
  };

  return (
    <>
      <PageBackground>
        <PageWrapper>
          <Container>
            <AccountCard>
          <Row>
            <Col>
              <PageTitle>Account Information</PageTitle>
            </Col>
          </Row>
          <Row>
            <Col>
              <UpdateProfile>
                <UpdateProfileButton onClick={() => setEditUserNameModal(true)} className={classes.margin}>
                  <Edit size={18} /> &nbsp; Update Profile
                </UpdateProfileButton>
              </UpdateProfile>

              <Divider />
              <Paragraph>
                <span>
                  User Name: <b>{userName}</b>
                </span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>
                  Email Address: <b>{authContext.getUserEmail()}</b>
                </span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>
                  User Phone: <b>{userPhone}</b>
                </span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>
                  Client Name: <b>{clientName}</b>
                </span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>
                  Client Address: <b>{clientAddress}</b>
                </span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>Account Created: {creationTime()}</span>
              </Paragraph>
              <Divider />
              <Paragraph>
                <span>Membership Tier: {renderTierBadge(authContext.getUserTier())}</span>
              </Paragraph>
              <Divider />
            </Col>
          </Row>
          <Row>
            <Col>
              <Paragraph>
                <span>
                  Click <a href="/">here</a> to return to CERES
                </span>
              </Paragraph>
              <Divider />
            </Col>
          </Row>
            </AccountCard>
          </Container>
        </PageWrapper>
      </PageBackground>
      <Footer />

      <EditUserModal
        dialogClassName="ceres--subscription-list"
        show={editUserNameModal}
        onHide={() => setEditUserNameModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Profile</Modal.Title>
        </Modal.Header>
        <EditUserPanelBody>
          <Form>
            <EditUserPanelItem>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={userName}
                  onChange={(e: any) => setUserName(e.target.value)}
                  placeholder="Enter name"
                />
              </Form.Group>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  value={userPhone}
                  onChange={(e: any) => setUserPhone(e.target.value)}
                  placeholder="Enter phone number"
                />
              </Form.Group>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Client Name</Form.Label>
                <Form.Control
                  type="text"
                  value={clientName}
                  onChange={(e: any) => setClientName(e.target.value)}
                  placeholder="Enter client name"
                />
              </Form.Group>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Client Address</Form.Label>
                <Form.Control
                  type="text"
                  value={clientAddress}
                  onChange={(e: any) => setClientAddress(e.target.value)}
                  placeholder="Enter client address"
                />
              </Form.Group>
            </EditUserPanelItem>
          </Form>
        </EditUserPanelBody>
        <Modal.Footer>
          <EditUserCloseButton variant="secondary" onClick={() => setEditUserNameModal(false)} disabled={isLoading}>
            Close
          </EditUserCloseButton>
          <EditUserSaveButton
            variant="primary"
            onClick={() => {
              setIsLoading(true);
              updateDoc(doc(db, 'users', userinfo.uid), {
                  name: userName,
                  phone: userPhone,
                  clientname: clientName,
                  address: clientAddress,
                })
                .then(() => {
                  toast.success(`Saved user information successfully`);
                  updateUserNameInContext(userName);
                })
                .catch(() => {
                  toast.error(`Error while saving user information`);
                })
                .finally(() => {
                  setIsLoading(false);
                  setEditUserNameModal(false);
                });
            }}
          >
            {isLoading ? <Spinner size="sm" variant="light" animation="border" /> : 'Save changes'}
          </EditUserSaveButton>
        </Modal.Footer>
      </EditUserModal>
    </>
  );
};

export default withAuthContext(Account);
